import React from 'react';
import clsx from 'clsx';
import * as Dialog from '@radix-ui/react-dialog';

const SIDE_CLASSES = {
  right: 'inset-y-0 right-0 h-full border-l',
  left: 'inset-y-0 left-0 h-full border-r',
  bottom: 'inset-x-0 bottom-0 border-t rounded-t-xl',
};

const SIZE_CLASSES = {
  sm: {
    right: 'w-full sm:max-w-sm',
    left: 'w-full sm:max-w-sm',
    bottom: 'h-[60vh] sm:h-[52vh]',
  },
  md: {
    right: 'w-full sm:max-w-md',
    left: 'w-full sm:max-w-md',
    bottom: 'h-[68vh] sm:h-[60vh]',
  },
  lg: {
    right: 'w-full sm:max-w-lg',
    left: 'w-full sm:max-w-lg',
    bottom: 'h-[75vh] sm:h-[68vh]',
  },
  xl: {
    right: 'w-full sm:max-w-xl',
    left: 'w-full sm:max-w-xl',
    bottom: 'h-[82vh] sm:h-[74vh]',
  },
  full: {
    right: 'w-full',
    left: 'w-full',
    bottom: 'h-[100dvh] rounded-none',
  },
};

function SheetHeader({ className, children, ...props }) {
  return (
    <div className={clsx('border-b border-gray-200 px-5 py-4', className)} {...props}>
      {children}
    </div>
  );
}

function SheetBody({ className, children, ...props }) {
  return (
    <div className={clsx('flex-1 overflow-y-auto px-5 py-4', className)} {...props}>
      {children}
    </div>
  );
}

function SheetFooter({ className, children, ...props }) {
  return (
    <div className={clsx('border-t border-gray-200 px-5 py-4', className)} {...props}>
      {children}
    </div>
  );
}

function SheetTitle({ className, ...props }) {
  return <Dialog.Title className={clsx('font-semibold text-gray-900', className)} {...props} />;
}

function SheetDescription({ className, ...props }) {
  return <Dialog.Description className={clsx('text-sm text-gray-500', className)} {...props} />;
}

export default function Sheet({
  open,
  onOpenChange,
  side = 'right',
  size = 'md',
  title,
  description,
  children,
  className,
  overlayStyle,
  contentStyle,
  onCloseAutoFocus,
  mobileFullScreen = true,
  closeOnBackdropClick = true,
  modal = true,
}) {
  const resolvedSide = SIDE_CLASSES[side] ? side : 'right';
  const resolvedSize = SIZE_CLASSES[size] ? size : 'md';

  const sizeClass = SIZE_CLASSES[resolvedSize][resolvedSide];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="mts-sheet-overlay fixed inset-0 z-[5000] bg-black/40"
          style={overlayStyle}
          onPointerDown={(event) => {
            if (!closeOnBackdropClick) {
              event.preventDefault();
            }
          }}
        />

        <Dialog.Content
          className={clsx(
            'mts-sheet-content fixed z-[5010] flex flex-col bg-white shadow-2xl outline-none',
            SIDE_CLASSES[resolvedSide],
            sizeClass,
            mobileFullScreen && size !== 'full' && resolvedSide !== 'bottom' && 'max-sm:w-full max-sm:h-[100dvh] max-sm:max-w-none',
            mobileFullScreen && size !== 'full' && resolvedSide === 'bottom' && 'max-sm:h-[100dvh] max-sm:rounded-none',
            className
          )}
          onCloseAutoFocus={onCloseAutoFocus}
          data-side={resolvedSide}
          style={contentStyle}
        >
          {title || description ? (
            <SheetHeader>
              <div className="min-w-0">
                {title ? (
                  <Dialog.Title className="truncate text-base font-semibold text-gray-900">{title}</Dialog.Title>
                ) : null}
                {description ? (
                  <Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description>
                ) : null}
              </div>
            </SheetHeader>
          ) : null}

          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

Sheet.Header = SheetHeader;
Sheet.Body = SheetBody;
Sheet.Footer = SheetFooter;
Sheet.Title = SheetTitle;
Sheet.Description = SheetDescription;

export { SheetHeader, SheetBody, SheetFooter, SheetTitle, SheetDescription };
